import React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material';
import { BarRounded, Line } from '@visx/shape';
import { scaleLinear, scaleBand, scaleOrdinal } from '@visx/scale';
import { useTooltip, useTooltipInPortal } from '@visx/tooltip';
import { Group } from '@visx/group';
import { GridRows } from '@visx/grid';
import { AxisBottom, AxisLeft } from '@visx/axis';
import TooltipWrapper from './TooltipWrapper';

let tooltipTimeout;
const defaultMargin = { top: 60, right: 0, bottom: 60, left: 40 };

const tickFormat = (t) => {
  const par = ')';
  const index = t.indexOf(par);
  const label = t.substring(index + 1); //returns the axis label without col-index

  return label;
};

const getLabel = ({ label }) => label;

const BarChart = function BarChart({
  width = 800,
  height = 300,
  colors,
  keys,
  dataToShow = [],
  margin = defaultMargin,
  showTarget = false,
  TooltipContent = null,
  minTickWidth = undefined,
  stacked = false,
  yTickFormat = (v) => v,
  universalTargetLineValue = undefined,
  // Render prop called once per label when showTarget=true.
  // Receives { x, y, target, value, label } — return a ReactNode or null.
  // x/y are the SVG coordinates above the tallest bar for that label.
  // value is the column total of the series that owns the target.
  renderTargetIcon = null,
}) {
  const theme = useTheme();

  // Always work with an array of series internally
  const normalizedData = Array.isArray(dataToShow[0])
    ? dataToShow
    : [dataToShow];

  const {
    tooltipOpen,
    tooltipTop,
    tooltipLeft,
    tooltipData,
    hideTooltip,
    showTooltip,
  } = useTooltip();
  const { containerRef } = useTooltipInPortal({
    scroll: true,
    detectBounds: true,
  });

  // Support flat keys/colors (shared by all series) or per-series arrays
  const isPerSeries = Array.isArray(keys?.[0]);
  const getSeriesKeys = (i) => (isPerSeries ? (keys[i] ?? keys[0]) : keys);
  const getSeriesColors = (i) =>
    isPerSeries ? (colors[i] ?? colors[0]) : colors;
  const isSeriesStacked = (i) =>
    Array.isArray(stacked) ? !!stacked[i] : !!stacked;

  // One color scale per series (key → color)
  const seriesColorScales = normalizedData.map((_, i) =>
    scaleOrdinal({ domain: getSeriesKeys(i), range: getSeriesColors(i) })
  );
  // Merged color scale used by TooltipContent
  const colorScale = scaleOrdinal({
    domain: isPerSeries ? keys.flat() : keys,
    range: isPerSeries ? colors.flat() : colors,
  });

  // Y domain: for stacked series use the column *total*, for overlapping use the *max single value*
  const barsDomainMax = Math.max(
    ...normalizedData.flatMap((series, si) => {
      const sKeys = getSeriesKeys(si);
      const isStacked = isSeriesStacked(si);
      return normalizedData[0].map(({ label: labelName }) => {
        let acc = 0;
        series.forEach((item) => {
          if (item.label !== labelName) return;
          sKeys.forEach((key) => {
            const v = item[key];
            if (typeof v === 'number')
              acc = isStacked ? acc + v : Math.max(acc, v);
          });
        });
        return acc;
      });
    })
  );
  const targetsDomainMax = Math.max(
    ...normalizedData.flatMap((series) =>
      series.map((item) => (typeof item.target === 'number' ? item.target : 0))
    )
  );
  const yDomainMax = showTarget
    ? Math.max(barsDomainMax, targetsDomainMax)
    : barsDomainMax;

  // Scales
  const xScale = scaleBand({
    domain: normalizedData[0].map(getLabel),
    paddingInner: 0.1,
    paddingOuter: 0.4,
  });
  const yScale = scaleLinear({ domain: [0, yDomainMax] });

  // Bounds
  const tickCount = normalizedData[0]?.length ?? 0;
  const svgWidth = minTickWidth
    ? Math.max(width, tickCount * minTickWidth + margin.left + margin.right)
    : width;
  const xMax = svgWidth - margin.left - margin.right;
  const yMax = height - margin.top - margin.bottom;

  xScale.rangeRound([0, xMax]);
  yScale.range([yMax, 0]);

  // Each series gets an equal horizontal slot; the bar fills 70% of it
  const numSeries = normalizedData.length;
  const slot = xScale.bandwidth() / numSeries;
  const barW = slot * 0.7;

  // ── Unified render loop ────────────────────────────────────────────────────
  // For every series × every label we produce one <Group> that contains
  // either stacked segments or overlapping bars depending on isSeriesStacked.
  const renderBars = normalizedData.flatMap((series, seriesIndex) => {
    const isStacked = isSeriesStacked(seriesIndex);
    const sKeys = getSeriesKeys(seriesIndex);
    const sColorScale = seriesColorScales[seriesIndex];

    return normalizedData[0]
      .map(({ label: labelName, minimumReached }, labelIndex) => {
        // Collapse all items that share this label into one { key: value } map.
        // A series can have multiple items per label (one item per key).
        const merged = {};
        let details;
        series.forEach((item) => {
          if (item.label !== labelName) return;
          sKeys.forEach((key) => {
            if (typeof item[key] === 'number') merged[key] = item[key];
          });
          if (item.details != null) details = item.details;
        });

        // Only keep keys that have a real positive value
        const activeEntries = sKeys
          .filter((k) => typeof merged[k] === 'number' && merged[k] > 0)
          .map((k) => ({
            key: k,
            value: merged[k],
            color:
              minimumReached || minimumReached === undefined
                ? sColorScale(k)
                : '#b3b3b3',
          }));

        if (activeEntries.length === 0) return null;

        // x position: offset by series slot, centered
        const xPos = xScale(labelName) + seriesIndex * slot + (slot - barW) / 2;

        // Overlapping bars: render tallest first so it sits behind shorter ones
        const renderEntries = isStacked
          ? activeEntries
          : [...activeEntries].sort((a, b) => b.value - a.value);

        let cumulative = 0;

        return (
          <Group key={`series-${seriesIndex}-label-${labelIndex}`} top={-5}>
            {renderEntries.map(({ key, value, color }, i) => {
              let barY, barH;
              if (isStacked) {
                // SVG y grows downward: segment goes from yScale(cum+val) up to yScale(cum)
                barY = yScale(cumulative + value);
                barH = yScale(cumulative) - barY;
                cumulative += value;
              } else {
                barY = yScale(value);
                barH = yMax - barY;
              }
              if (barH <= 0) return null;

              // Stacked: round only the outermost corners of the full column
              // Overlapping: each bar is fully rounded
              const roundTop = !isStacked || i === renderEntries.length - 1;
              const roundBottom = !isStacked || i === 0;

              return (
                <BarRounded
                  key={`bar-${seriesIndex}-${labelIndex}-${key}`}
                  radius={10}
                  top={roundTop}
                  bottom={roundBottom}
                  x={xPos}
                  y={barY}
                  height={barH}
                  width={barW}
                  fill={color}
                  onMouseLeave={() => {
                    tooltipTimeout = window.setTimeout(hideTooltip, 300);
                  }}
                  style={{
                    opacity:
                      minimumReached || minimumReached === undefined ? 1 : 0.6,
                  }}
                  onMouseMove={(event) => {
                    if (tooltipTimeout) clearTimeout(tooltipTimeout);
                    showTooltip({
                      tooltipData: {
                        key,
                        value,
                        color,
                        index: seriesIndex,
                        label: labelName,
                        details,
                        data: normalizedData[seriesIndex]?.find(
                          (i) => i.label === labelName
                        ),
                      },
                      tooltipTop: event.pageY + 80,
                      tooltipLeft: event.pageX,
                    });
                  }}
                />
              );
            })}

            {/* target line rendered in renderTargetIcons, spanning full label width */}
          </Group>
        );
      })
      .filter(Boolean);
  });

  const renderTargetIcons =
    showTarget || renderTargetIcon
      ? normalizedData[0]
          .map(({ label: labelName }, labelIndex) => {
            // Look up target value from item data (may be absent)
            let target;
            let targetSeriesIndex = -1;
            normalizedData.forEach((series, si) => {
              series.forEach((item) => {
                if (item.label === labelName && item.target != null) {
                  target = item.target;
                  targetSeriesIndex = si;
                }
              });
            });

            // Target-dependent values — only computed when a target exists
            let targetSeriesValue = 0;

            if (target != null) {
              // The value to compare against the target is the column total of the
              // series that actually carries the target (stacked → sum, overlapping → max)
              const targetSeries = normalizedData[targetSeriesIndex];
              const targetSeriesKeys = getSeriesKeys(targetSeriesIndex);
              const isTargetStacked = isSeriesStacked(targetSeriesIndex);
              targetSeries.forEach((item) => {
                if (item.label !== labelName) return;
                targetSeriesKeys.forEach((key) => {
                  const v = item[key];
                  if (typeof v === 'number')
                    targetSeriesValue = isTargetStacked
                      ? targetSeriesValue + v
                      : Math.max(targetSeriesValue, v);
                });
              });
            }

            // Position is always available regardless of target
            let maxValue = Math.max(
              ...normalizedData.flatMap((series, si) =>
                getSeriesKeys(si).flatMap((key) =>
                  series
                    .filter((item) => item.label === labelName)
                    .map((item) =>
                      typeof item[key] === 'number' ? item[key] : 0
                    )
                )
              )
            );
            if (maxValue < target) maxValue = target;
            const centerX = xScale(labelName) + xScale.bandwidth() / 2;
            const lineX0 = xScale(labelName);
            const lineX1 = xScale(labelName) + xScale.bandwidth();
            const topY = yScale(maxValue) - 30;
            const targetY = target != null ? yScale(target) - 5 : 0;

            const showLine = showTarget && target != null;
            if (!showLine && !renderTargetIcon) return null;

            const targetSeriesData =
              targetSeriesIndex >= 0
                ? normalizedData[targetSeriesIndex]?.find(
                    (item) => item.label === labelName
                  )
                : undefined;

            return (
              <>
                {showLine && (
                  <Line
                    key={`target-line-${labelIndex}`}
                    strokeWidth="3"
                    stroke={theme.palette.graphs.curveLine}
                    from={{ x: lineX0, y: targetY }}
                    to={{ x: lineX1, y: targetY }}
                    onMouseLeave={() => {
                      tooltipTimeout = window.setTimeout(hideTooltip, 300);
                    }}
                    onMouseMove={(event) => {
                      if (tooltipTimeout) clearTimeout(tooltipTimeout);
                      showTooltip({
                        tooltipData: {
                          target,
                          color: theme.palette.graphs.curveLine,
                        },
                        tooltipTop: event.pageY,
                        tooltipLeft: event.pageX,
                      });
                    }}
                  />
                )}
                {renderTargetIcon &&
                  renderTargetIcon({
                    // SVG coords — add margin.left/top to convert to page space if needed
                    x: centerX,
                    y: topY,
                    target,
                    value: targetSeriesValue,
                    label: labelName,
                    data: targetSeriesData,
                  })}
              </>
            );
          })
          .filter(Boolean)
      : null;

  return (
    <div
      style={{
        position: 'relative',
        overflowY: minTickWidth ? 'hidden' : 'visible',
        overflowX: minTickWidth ? 'auto' : 'visible',
        width,
        height,
      }}
    >
      <svg width={svgWidth} height={height} ref={containerRef}>
        <GridRows
          x1={0}
          top={margin.top - 5}
          left={margin.left + 20}
          scale={yScale}
          numTicks={5}
          stroke={theme.palette.greys.lighter}
          strokeDasharray={5}
          width={xMax}
          height={yMax - margin.top - margin.bottom}
        />
        <Group top={margin.top} left={margin.left}>
          {renderBars}
          {renderTargetIcons}

          {/* Universal target line spanning full graph width */}
          {universalTargetLineValue != null && (
            <>
              <Line
                strokeWidth="3"
                stroke={theme.palette.graphs.curveLine}
                from={{ x: 0, y: yScale(universalTargetLineValue) }}
                to={{ x: xMax, y: yScale(universalTargetLineValue) }}
              />
              {/* Purple tick mark on AxisLeft */}
              <text
                x={60}
                y={yScale(universalTargetLineValue) - 8}
                textAnchor="end"
                fontSize={18}
                fontFamily="Roboto"
                fill={theme.palette.graphs.curveLine}
                fontWeight="bold"
              >
                {yTickFormat(universalTargetLineValue)}
              </text>
            </>
          )}
        </Group>

        <AxisBottom
          top={yMax + margin.top}
          left={margin.left}
          strokeDasharray={6}
          hideTicks
          hideAxisLine
          numTicks={normalizedData[0].length}
          scale={xScale}
          tickFormat={tickFormat}
          stroke={theme.palette.greys.lighter}
          strokeWidth={1.5}
          tickStroke={theme.palette.greys.lighter}
          tickLabelProps={() => ({
            fill: theme.palette.greys.lighter,
            textAnchor: 'middle',
            fontSize: 14,
            fontFamily: 'Roboto',
          })}
        />
        <AxisLeft
          hideTicks
          hideAxisLine
          numTicks={5}
          top={margin.top}
          left={margin.left}
          scale={yScale}
          strokeWidth={1.5}
          tickFormat={yTickFormat}
          tickLabelProps={() => ({
            fill: theme.palette.greys.lighter,
            textAnchor: 'middle',
            fontSize: 14,
            fontFamily: 'Roboto',
            textOverflow: 'ellipsis',
          })}
        />
      </svg>
      <TooltipWrapper
        open={tooltipOpen}
        data={tooltipData}
        tooltipPosition={{ top: tooltipTop, left: tooltipLeft }}
        portal
      >
        {TooltipContent && (
          <TooltipContent data={tooltipData} colorScale={colorScale} />
        )}
      </TooltipWrapper>
    </div>
  );
};

BarChart.propTypes = {
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  dataToShow: PropTypes.array,
  margin: PropTypes.object,
  colors: PropTypes.array.isRequired,
  keys: PropTypes.array.isRequired,
  stacked: PropTypes.bool,
  universalTargetLineValue: PropTypes.number,
  yTickFormat: PropTypes.func,
};

export default BarChart;
